import Vue from "vue";
import CoinList from "@/components/widgets/coin-list";

// เผื่อเศษทศนิยมให้ตรงกับฝั่ง backend (PownganController::issueProduction)
// backend ใช้ tolerance 0.01 ถ้าหน้าจอเข้มกว่านี้จะกดปุ่มไม่ได้ทั้งที่ backend ยอม
const EPS = 0.01;

// ชนิดสินค้าที่ผลิตจากม้วน: 1 = แผ่นเมทัลชีท, 2 = ครอบเมทัลชีท
// ที่เหลือ (สกรู 4 / น้ำยา 3 / ฟอยล์ 5 / ETC 7 / ค่าบริการ 8 ...) เป็นของซื้อมาขายไป
// ไม่ได้รีดจากม้วน จึงไม่ต้องออกใบผลิต — กติกาเดียวกับใบผลิตปกติ (production/widget-create)
const ROLL_FED_TYPES = [1, 2];

export default {
  name: "PownganRollPicker",

  components: { CoinList },

  props: {
    show: { type: Boolean, default: false },
    // ใบสั่งโป๊ว (ต้องมี items)
    order: { type: Object, default: null },
    submitting: { type: Boolean, default: false },
  },

  data() {
    return {
      // rows = [{ item, rolls: [{...roll, cut}] }] — เฉพาะรายการที่ผลิตจากม้วน
      rows: [],
      // รายการที่ข้ามไป (สกรู ฯลฯ) — โชว์ให้รู้ว่าไม่ได้ลืม แต่ไม่ต้องเลือกม้วน
      skippedItems: [],
      isCoinOpened: false,
      coinIndex: -1,
    };
  },

  watch: {
    // เปิด modal ใหม่ = เริ่มเลือกใหม่ทุกครั้ง กันค่าค้างจากใบก่อนหน้า
    show(val) {
      if (val) this.buildRows();
    },
  },

  computed: {
    notReadyCount() {
      return this.rows.filter((r) => !this.isRowReady(r)).length;
    },

    allItemsReady() {
      return this.rows.length > 0 && this.rows.every((r) => this.isRowReady(r));
    },

    // ม้วนเดียวอาจถูกใช้หลายรายการ — ต้องรวมทั้งใบแล้วไม่เกินคงเหลือ
    // (backend รวมข้ามทุก group เหมือนกัน)
    overusedRolls() {
      const used = {};
      const info = {};
      this.rows.forEach((row) => {
        row.rolls.forEach((r) => {
          used[r.id] = (used[r.id] || 0) + this.rollUse(row, r.cut);
          info[r.id] = r;
        });
      });
      return Object.keys(used)
        .filter((id) => used[id] - this.num(info[id].remaining_length) > EPS)
        .map((id) => ({ roll: info[id], used: used[id] }));
    },

    blockReason() {
      if (!this.rows.length) return "";
      if (this.overusedRolls.length) {
        const first = this.overusedRolls[0];
        return `ม้วน ${first.roll.coil} ใช้รวมเกินความยาวคงเหลือ`;
      }
      if (!this.allItemsReady) return "";
      return "";
    },

    canSubmit() {
      return this.allItemsReady && this.overusedRolls.length === 0;
    },
  },

  methods: {
    num(v) {
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },

    // ตัวคูณการผลิตของ SKU (ครอบ G050 = 0.0547 ฯลฯ) — ตัดจากม้วนจริง = ความยาวสินค้า × ตัวคูณ
    // ต้องตรงกับ backend PownganController::issueProduction ($rollUse)
    // คนละค่ากับ roll_multiplier (ตัวคูณลอน) ที่ใช้คิดเงินเท่านั้น
    mult(row) {
      const sku = row && row.item && row.item.sku;
      const m = this.num(sku && sku.production_multiplier);
      return m > 0 ? m : 1;
    },

    // เมตรที่ใช้จากม้วนจริง ของความยาวสินค้า cut ในรายการ row
    rollUse(row, cut) {
      return this.num(cut) * this.mult(row);
    },

    fmt(v) {
      return this.num(v).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // รายการนี้ผลิตจากม้วนไหม
    // ห้ามใช้ is_insulator ตัดสิน — ฝั่งใบสั่งโป๊วมันแปลว่า "รายการนี้ติดฉนวน"
    // ซึ่งสกรูก็ติ๊กได้ ไม่ได้แปลว่าเป็นสินค้าประเภทฉนวน
    isRollFed(item) {
      const t = item && item.sku ? Number(item.sku.main_product_id) : null;
      return ROLL_FED_TYPES.indexOf(t) !== -1;
    },

    buildRows() {
      const items = (this.order && this.order.items) || [];
      this.rows = items
        .filter((item) => this.isRollFed(item))
        .map((item) => ({ item, rolls: [] }));
      this.skippedItems = items.filter((item) => !this.isRollFed(item));
    },

    // ชื่อชนิดสินค้าไว้บอกเหตุผลที่ข้ามรายการนั้น
    typeName(item) {
      return (item && item.sku && item.sku.main_product && item.sku.main_product.name) || "ไม่ใช่แผ่น/ครอบ";
    },

    // ---- ยอดต่อรายการ ----
    pickedSum(row) {
      return row.rolls.reduce((s, r) => s + this.num(r.cut), 0);
    },

    remaining(row) {
      return this.num(row.item.total_length) - this.pickedSum(row);
    },

    isRowReady(row) {
      if (!row.rolls.length) return false;
      // ทุกม้วนต้องกรอกความยาว > 0 (backend ปฏิเสธ length <= 0)
      if (row.rolls.some((r) => this.num(r.cut) <= 0)) return false;
      return Math.abs(this.remaining(row)) <= EPS;
    },

    // ---- แถบความคืบหน้า ----
    progressPct(row) {
      const need = this.num(row.item.total_length);
      if (need <= 0) return 0;
      return Math.min(100, (this.pickedSum(row) / need) * 100);
    },

    progressClass(row) {
      const diff = this.remaining(row);
      if (diff < -EPS) return "bg-danger";       // เลือกเกิน
      if (Math.abs(diff) <= EPS) return "bg-success"; // พอดี
      return "bg-warning";                        // ยังขาด
    },

    statusText(row) {
      const diff = this.remaining(row);
      const unit = row.item.unit_name || "ม.";
      if (!row.rolls.length) return "ยังไม่ได้เลือกม้วน";
      if (row.rolls.some((r) => this.num(r.cut) <= 0)) return "กรุณากรอกความยาวที่จะตัดให้ครบทุกม้วน";
      if (diff > EPS) return `ขาดอีก ${this.fmt(diff)} ${unit}`;
      if (diff < -EPS) return `เกินไป ${this.fmt(Math.abs(diff))} ${unit}`;
      return "ครบแล้ว";
    },

    statusTextClass(row) {
      const diff = this.remaining(row);
      if (!row.rolls.length) return "text-muted";
      if (Math.abs(diff) <= EPS && !row.rolls.some((r) => this.num(r.cut) <= 0)) return "text-success";
      if (diff < -EPS) return "text-danger";
      return "text-warning";
    },

    statusIcon(row) {
      const diff = this.remaining(row);
      if (!row.rolls.length) return "mdi mdi-circle-outline";
      if (Math.abs(diff) <= EPS && !row.rolls.some((r) => this.num(r.cut) <= 0)) return "mdi mdi-check-circle";
      return "mdi mdi-alert-circle-outline";
    },

    // ---- เช็คม้วนใช้เกิน (รวมทั้งใบ) ----
    totalUsedOfRoll(rollId) {
      let sum = 0;
      this.rows.forEach((row) => {
        row.rolls.forEach((r) => {
          if (r.id === rollId) sum += this.rollUse(row, r.cut);
        });
      });
      return sum;
    },

    overUsed(r) {
      return this.totalUsedOfRoll(r.id) - this.num(r.remaining_length) > EPS;
    },

    // ---- เลือกม้วน ----
    openCoinList(idx) {
      this.coinIndex = idx;
      this.isCoinOpened = true;
    },

    onCoinListClose(payload) {
      this.isCoinOpened = false;
      if (!payload || !payload.status) return;

      const row = this.rows[payload.index];
      if (!row) return;

      // multiple = true ส่งกลับเป็น array
      const picked = Array.isArray(payload.data) ? payload.data : [payload.data];

      picked.forEach((roll) => {
        if (!roll) return;
        // กันเลือกม้วนซ้ำในรายการเดียวกัน (ม้วนเดียวกันข้ามรายการยังได้)
        if (row.rolls.some((r) => r.id === roll.id)) return;

        // remaining_length มาจาก backend แล้ว (null = ยังเต็มม้วน ถูกคำนวณให้ที่ model)
        // เผื่อ endpoint เก่าที่ยังไม่มีฟิลด์นี้ fallback เป็น length
        const remaining =
          roll.remaining_length !== undefined && roll.remaining_length !== null
            ? this.num(roll.remaining_length)
            : this.num(roll.length);

        // ตั้งค่าเริ่มต้นให้ฉลาด: เติมเท่าที่ยังขาด แต่ไม่เกินที่ม้วนนี้เหลือ
        const need = Math.max(0, this.remaining(row));
        const cut = Math.min(need, remaining / this.mult(row));

        row.rolls.push({
          ...roll,
          remaining_length: remaining,
          cut: cut > 0 ? Number(cut.toFixed(2)) : 0,
        });
      });
    },

    removeRoll(row, ri) {
      row.rolls.splice(ri, 1);
    },

    // เติมส่วนที่ขาดให้ม้วนสุดท้าย (ไม่เกินที่ม้วนนั้นเหลือ)
    autoFill(row) {
      const last = row.rolls[row.rolls.length - 1];
      if (!last) return;
      const need = this.remaining(row);
      if (need <= 0) return;

      // เหลือให้ใช้ของม้วนนี้ (เมตรม้วน) = คงเหลือ - ที่ถูกใช้ในรายการอื่น แล้วแปลงกลับเป็นความยาวสินค้า
      const m = this.mult(row);
      const usedElsewhere = this.totalUsedOfRoll(last.id) - this.rollUse(row, last.cut);
      const canAdd = (this.num(last.remaining_length) - usedElsewhere) / m - this.num(last.cut);

      const add = Math.min(need, Math.max(0, canAdd));
      last.cut = Number((this.num(last.cut) + add).toFixed(2));
      this.$forceUpdate();
    },

    onCutChanged() {
      // v-model อัปเดต reactive อยู่แล้ว — hook ไว้เผื่อ logic เพิ่มภายหลัง
    },

    // ---- ส่งข้อมูล ----
    // แยกใบผลิตตามสเปกเดียวกับ backend: ลอน/ครอบ (main_product_id) แยกจากฉนวน PU/อื่น
    // 1 กลุ่ม = 1 ใบผลิต
    buildGroups() {
      const groups = {};

      this.rows.forEach((row) => {
        const item = row.item;

        // จัดกลุ่มตามชนิดสินค้า (แผ่น/ครอบ) + สเปกฉนวนที่ติดมากับแผ่นนั้น
        //
        // ห้ามแยกด้วย is_insulator เดี่ยว ๆ — ฝั่งใบสั่งโป๊วมันแปลว่า "แผ่นนี้ติดฉนวน"
        // แผ่นติดฉนวนก็ยังเป็นแผ่น ต้องอยู่กลุ่มแผ่น (backend คิด type จาก main_product_id)
        // ส่วนสีท้อง/ชนิด/ความหนาที่ต่างกันต้องคนละใบ backend แยกซ้ำให้อีกชั้นอยู่แล้ว
        const t = (item.sku && item.sku.main_product_id) || 1;
        const insKey = item.is_insulator
          ? ["INS", item.isPE ? 1 : 0, item.isPU ? 1 : 0, item.thickness || "",
             item.insulator_material || "", (item.insulator_detail || "").trim()].join("|")
          : "NOINS";
        const key = "T" + t + "|" + insKey;

        if (!groups[key]) groups[key] = [];

        row.rolls.forEach((r) => {
          groups[key].push({
            powngan_order_item_id: item.id,
            roll_id: r.id,
            roll_name: this.rollLabel(r),
            length: Number(this.num(r.cut).toFixed(2)),
          });
        });
      });

      return Object.keys(groups).map((k) => groups[k]);
    },

    // ข้อความม้วนที่จะขึ้นบน PDF ใบผลิต
    rollLabel(r) {
      const parts = [];
      if (r.size) parts.push(r.size);
      if (r.color) parts.push(r.color);
      if (r.brand) parts.push(r.brand);
      if (r.coil) parts.push(r.coil);
      return parts.join(" ");
    },

    onSubmit() {
      if (!this.canSubmit) return;
      this.$emit("onPownganRollPickerSubmit", { groups: this.buildGroups() });
    },

    onClose() {
      this.$emit("onPownganRollPickerClose");
    },
  },
};
